import { Shield } from "lucide-react";
import { MarketingHeader } from "@/components/marketing-header";
import { Footer } from "@/components/footer";
import { useSeo, SEO_PAGES } from "@/hooks/use-seo";

const LAST_UPDATED = "April 18, 2026";

const SECTIONS = [
  {
    title: "1. Introduction",
    body: (
      <>
        <p>
          UnfoldWin ("we", "us", or "our") builds an AI-assisted journaling product
          that helps people turn what they read, watch, and feel into real change.
          This Privacy Policy explains what information we collect, how we use it,
          and the choices you have.
        </p>
        <p>
          By using UnfoldWin you agree to the practices described here. If you do
          not agree, please do not use the service.
        </p>
      </>
    ),
  },
  {
    title: "2. Information we collect",
    body: (
      <>
        <p className="font-medium text-foreground">Account information</p>
        <p>
          When you sign up we collect your name, email address, and authentication
          credentials managed through our identity provider.
        </p>
        <p className="font-medium text-foreground mt-4">Content you create</p>
        <p>
          Journal entries, reflections, voice recordings, pasted links, and any
          other content you provide while using the product.
        </p>
        <p className="font-medium text-foreground mt-4">Usage data</p>
        <p>
          Device and browser information, IP address, pages visited, and
          interactions with features. We use this to keep the product fast,
          secure, and reliable.
        </p>
      </>
    ),
  },
  {
    title: "3. How we use your information",
    body: (
      <ul className="list-disc pl-5 space-y-1.5">
        <li>To provide, personalise, and improve the service.</li>
        <li>To generate AI reflections, summaries, and journal entries on your behalf.</li>
        <li>To communicate with you about product updates, security notices, and support.</li>
        <li>To detect, prevent, and address fraud or abuse.</li>
        <li>To comply with legal obligations.</li>
      </ul>
    ),
  },
  {
    title: "4. AI processing",
    body: (
      <p>
        To produce reflections and journal entries we send the content you provide
        to third-party AI providers (for example, OpenAI). These providers process
        your content on our behalf under their own privacy and data-handling terms.
        We do not use your private journal content to train public models.
      </p>
    ),
  },
  {
    title: "5. Sharing and disclosure",
    body: (
      <p>
        We share information with service providers who help us run UnfoldWin —
        hosting, authentication, analytics, and AI inference — under contracts
        that protect your data. We do not sell your personal information. We may
        disclose information when required by law or to protect our rights and
        the safety of our users.
      </p>
    ),
  },
  {
    title: "6. Data retention",
    body: (
      <p>
        We keep your data for as long as your account is active or as needed to
        provide the service. You can delete your journal entries at any time.
        If you delete your account, we remove associated personal data within a
        reasonable period, except where retention is required by law.
      </p>
    ),
  },
  {
    title: "7. Your rights",
    body: (
      <p>
        Depending on where you live, you may have the right to access, correct,
        export, or delete your personal data, and to object to or restrict
        certain processing. To exercise these rights, email us at{" "}
        <a
          href="mailto:privacy@unfoldwin.com"
          className="text-primary underline underline-offset-2"
        >
          privacy@unfoldwin.com
        </a>
        .
      </p>
    ),
  },
  {
    title: "8. Security",
    body: (
      <p>
        We use industry-standard safeguards to protect your information in transit
        and at rest. No method is 100% secure, but we work hard to keep your
        reflections private and your account safe.
      </p>
    ),
  },
  {
    title: "9. Children's privacy",
    body: (
      <p>
        UnfoldWin is not intended for children under 13 (or the equivalent age in
        your jurisdiction). We do not knowingly collect personal information from
        children.
      </p>
    ),
  },
  {
    title: "10. Changes to this policy",
    body: (
      <p>
        We may update this policy from time to time. When we do, we'll revise the
        "Last updated" date and, for significant changes, notify you in-product
        or by email.
      </p>
    ),
  },
  {
    title: "11. Contact us",
    body: (
      <p>
        Questions about this policy? Reach us at{" "}
        <a
          href="mailto:privacy@unfoldwin.com"
          className="text-primary underline underline-offset-2"
        >
          privacy@unfoldwin.com
        </a>
        .
      </p>
    ),
  },
];

export default function PrivacyPage() {
  useSeo(SEO_PAGES["/privacy"]);
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MarketingHeader />

      <main className="flex-1">
        <section className="py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-5">
            <div className="mb-10">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-5">
                <Shield className="w-3.5 h-3.5" />
                Legal
              </span>
              <h1 className="font-display text-4xl md:text-5xl font-semibold text-foreground mb-3">
                Privacy Policy
              </h1>
              <p className="text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
            </div>

            <div className="space-y-8 text-[15px] leading-relaxed text-muted-foreground">
              {SECTIONS.map((section) => (
                <section key={section.title}>
                  <h2 className="font-display text-xl font-medium text-foreground mb-3">
                    {section.title}
                  </h2>
                  <div className="space-y-3">{section.body}</div>
                </section>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
